import React, { useState } from 'react';
import { Box, Stack, Typography, useMediaQuery } from '@mui/material';

import { BasicDialog, ChatMessage, CustomButton } from '../../../../components';
import keyboardIcon from '../../../../assets/keyboard.png';

type MobileDialogAction = 'sendLink' | 'proceed' | null;

interface IProps {
  showTTDialog: boolean;
  setShowTTDialog: React.Dispatch<React.SetStateAction<boolean>>;
  handleCreateTestTask: (simulated: boolean, sendLinkByEmail?: boolean) => void;
  createTTIsLoading: boolean;
}

export const CodingTTMobileDialog: React.FC<IProps> = ({
  showTTDialog,
  setShowTTDialog,
  handleCreateTestTask,
  createTTIsLoading,
}) => {
  const isLandscape = useMediaQuery('(orientation: landscape)');
  const [activeAction, setActiveAction] = useState<MobileDialogAction>(null);

  const onSendLink = () => {
    setActiveAction('sendLink');
    handleCreateTestTask(false, true);
  };

  const onProceedAnyway = () => {
    setActiveAction('proceed');
    handleCreateTestTask(false);
  };

  const isSendLinkLoading = createTTIsLoading && activeAction === 'sendLink';
  const isProceedLoading = createTTIsLoading && activeAction === 'proceed';

  return (
    <BasicDialog
      isOpen={showTTDialog}
      handleClose={() => setShowTTDialog(false)}
      maxWidth={isLandscape ? '85vw' : undefined}
    >
      <Box display="flex" flexDirection="column" gap={isLandscape ? 1 : 0}>
        {!isLandscape && (
          <Box display="flex" justifyContent="center">
            <img src={keyboardIcon} alt="keyboard" />
          </Box>
        )}
        <Typography
          variant="h2"
          textAlign="center"
          sx={isLandscape ? { fontSize: '1rem', lineHeight: 1.2 } : { pt: 4 }}
        >
          You&apos;re going to need
          {!isLandscape && <br />} a keyboard
        </Typography>
        <Box
          display="flex"
          flexDirection={isLandscape ? 'row' : 'column'}
          gap={isLandscape ? 1.5 : 0}
          alignItems={isLandscape ? 'center' : undefined}
        >
          {isLandscape && (
            <Box
              flexShrink={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
              width="90px"
            >
              <img
                src={keyboardIcon}
                alt="keyboard"
                style={{ width: '75px' }}
              />
            </Box>
          )}
          <Box flex={isLandscape ? 1 : undefined} minWidth={0}>
            <ChatMessage
              isInCenter
              sx={
                isLandscape
                  ? {
                      '& .children-wrapper': {
                        fontSize: '0.8rem',
                        lineHeight: 1.4,
                      },
                    }
                  : undefined
              }
            >
              Our test tasks use HackerRank, a web IDE which requires a desktop
              browser and a trusty keyboard.
            </ChatMessage>
          </Box>
        </Box>
        <Typography
          variant={isLandscape ? 'body2' : 'body1'}
          textAlign="center"
          color={isLandscape ? 'text.primary' : undefined}
          sx={isLandscape ? { fontSize: '0.75rem', mb: 0.5 } : { my: 4 }}
        >
          We will email you a link to the task.
          {!isLandscape && <br />} Please use it once you&apos;re all set up and
          {!isLandscape && <br />} ready to ace the test.
        </Typography>
        <Stack
          direction={isLandscape ? 'row' : 'column'}
          spacing={isLandscape ? 1 : 2}
          justifyContent="center"
          sx={
            isLandscape
              ? {
                  '& .ae-custom-button_front': { padding: '0.35rem 1rem' },
                  '& .ae-custom-button_text': {
                    fontSize: '0.75rem',
                    whiteSpace: 'nowrap',
                  },
                }
              : undefined
          }
        >
          <CustomButton
            label="Send me a link"
            fullWidth={!isLandscape}
            onClick={onSendLink}
            isLoading={isSendLinkLoading}
            isDisabled={isProceedLoading}
            color="primary"
          />
          <CustomButton
            label="Proceed anyway"
            fullWidth={!isLandscape}
            onClick={onProceedAnyway}
            isLoading={isProceedLoading}
            isDisabled={isSendLinkLoading}
            color="secondary"
          />
        </Stack>
      </Box>
    </BasicDialog>
  );
};
